import {
  ExperienceChildConfigKind,
  ExperienceLayouts,
} from '@/types/gql.generated';

/**
 * What a view *is*, not how its template is built — so names collide on purpose:
 * the 5+2 week and the seven-column sheet are both "Week", every month-card box
 * count is "Grid".
 *
 * Exhaustive by type, so a regenerated enum fails to compile until someone
 * decides what a new layout is called.
 */
const VIEW_BASE_NAMES: Record<ExperienceLayouts, string> = {
  // plannerCalendar is the legacy alias live rows still carry; printableCalendar
  // is the retired printable template.
  [ExperienceLayouts.PlannerMonth]: 'Month',
  [ExperienceLayouts.PlannerCalendar]: 'Month',
  [ExperienceLayouts.PrintableCalendar]: 'Month',

  [ExperienceLayouts.PlannerWeek]: 'Week',
  [ExperienceLayouts.PrintableWeek]: 'Week',
  [ExperienceLayouts.Week]: 'Week',

  [ExperienceLayouts.DayByCategory]: 'Day by Category',

  [ExperienceLayouts.ListVerticalBulleted]: 'List',
  [ExperienceLayouts.ListVerticalBulletedCondensed]: 'List',
  [ExperienceLayouts.PrintableList]: 'List',
  [ExperienceLayouts.PrintableListVertical]: 'List',
  [ExperienceLayouts.PrintVertical]: 'List',
  [ExperienceLayouts.ListTable]: 'List',

  [ExperienceLayouts.PrintableYearly3BoxMonthCard]: 'Grid',
  [ExperienceLayouts.PrintableYearly6BoxMonthCard]: 'Grid',
  [ExperienceLayouts.PrintableYearlyMonthCard]: 'Grid',
  [ExperienceLayouts.Grid]: 'Grid',

  [ExperienceLayouts.ListVertical]: 'Cards',
  [ExperienceLayouts.ListVerticalWithImages]: 'Cards',
  [ExperienceLayouts.SideHeader]: 'Cards',
  [ExperienceLayouts.SideHeaderV2]: 'Cards',

  // AI-designed printable, rendered server-side.
  [ExperienceLayouts.PrintableCustom]: 'Custom',

  // Neither is creatable — planner is the *parent* layout. Here only to satisfy
  // the exhaustive Record.
  [ExperienceLayouts.SingleImage]: 'Poster',
  [ExperienceLayouts.Planner]: 'Planner',
};

/** Unprefixed, for the Add-a-view cards — their kind filter already says which. */
export const viewBaseName = (layoutId: ExperienceLayouts): string =>
  VIEW_BASE_NAMES[layoutId];

/**
 * Prefixed for paper, because the pickers list both kinds together. Never reads
 * `title` — renaming views is a separate feature.
 */
export const viewName = (view: {
  layoutId: ExperienceLayouts;
  kind: ExperienceChildConfigKind;
}): string => {
  const base = viewBaseName(view.layoutId);
  return view.kind === ExperienceChildConfigKind.Printable
    ? `Printable ${base}`
    : base;
};

type NamedView = {
  id: string;
  layoutId: ExperienceLayouts;
  kind: ExperienceChildConfigKind;
};

/**
 * The name a switcher shows, numbered on collision — "Week", "Week 2". A switcher
 * row is a name and an icon, which duplicates share, so without this two rows are
 * indistinguishable.
 *
 * Numbered from the full sibling list rather than the group being rendered, so
 * every surface calls a view the same thing.
 */
export const viewNameInPlanner = (
  view: NamedView,
  siblings: readonly NamedView[]
): string => {
  const base = viewName(view);
  const sameName = siblings.filter((s) => viewName(s) === base);
  if (sameName.length < 2) return base;

  const position = sameName.findIndex((s) => s.id === view.id);
  return position > 0 ? `${base} ${position + 1}` : base;
};
